import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiSearch, FiEdit, FiTrash2 } from "react-icons/fi";
import { AiOutlinePlus, AiOutlineClose } from "react-icons/ai";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomNavbar from "../CustomPermissions/CustomNavbar";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../config/api";

const ModeOfPaymentPage = () => {
  const [modes, setModes] = useState([]);
  const [filteredModes, setFilteredModes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [modeName, setModeName] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch all modes on component mount
  useEffect(() => {
    fetchModes();
  }, []);

  const fetchModes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(getApiUrl("/Advance/modes/"));
      setModes(response.data);
      setFilteredModes(response.data);
    } catch (error) {
      console.error("Error fetching modes of payment:", error);
      toast.error("Failed to fetch modes of payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle search input
  useEffect(() => {
    const filtered = modes.filter((mode) =>
      mode.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredModes(filtered);
  }, [searchQuery, modes]);

  // Open modal to add or edit a mode
  const handleAddMode = () => {
    setEditMode(null);
    setModeName("");
    setModalOpen(true);
  };

  const handleEditMode = (mode) => {
    setEditMode(mode);
    setModeName(mode.name);
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await axios.put(
          getApiUrl(`/Advance/modes/${editMode.id}/update/`),
          {
            name: modeName,
          }
        );
        toast.success("Mode updated successfully!");
      } else {
        await axios.post(getApiUrl("/Advance/modes/create/"), {
          name: modeName,
        });
        toast.success("Mode added successfully!");
      }
      fetchModes();
      setModalOpen(false);
    } catch (error) {
      console.error("Error saving mode of payment:", error);
      toast.error("Failed to save mode. Please try again.");
    }
  };

  // Handle delete action
  const handleDelete = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this mode of payment?")
    ) {
      try {
        await axios.delete(getApiUrl(`/Advance/modes/${id}/delete/`));
        toast.success("Mode deleted successfully!");
        fetchModes();
      } catch (error) {
        console.error("Error deleting mode of payment:", error);
        toast.error("Failed to delete mode. Please try again.");
      }
    }
  };

  return (
    <div className="flex min-h-screen">
      <CustomNavbar />
      <div className="flex-grow min-w-0 p-8">
        <ToastContainer position="top-right" autoClose={3000} />
        <h1 className="text-4xl font-bold text-gray-800 mb-8">
          Modes of Payment
        </h1>

        {/* Search and Add Mode Section */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <FiSearch className="text-gray-500 text-2xl" />
            <input
              type="text"
              placeholder="Search by mode name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 w-full md:w-64"
            />
          </div>
          <button
            onClick={handleAddMode}
            className="bg-blue-600 text-white px-6 py-2 rounded-md flex items-center gap-2 hover:bg-blue-700 shadow-lg transition-transform transform hover:scale-105"
          >
            <AiOutlinePlus /> Add Mode
          </button>
        </div>

        {/* Modes List */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] table-auto">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="py-4 px-6 text-left">#</th>
                  <th className="py-4 px-6 text-left">Mode Name</th>
                  <th className="py-4 px-6 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="3" className="py-8 text-center text-gray-600">
                      <div className="animate-spin border-t-4 border-blue-400 border-solid rounded-full w-8 h-8 mx-auto"></div>
                      <p className="mt-2">Loading...</p>
                    </td>
                  </tr>
                ) : filteredModes.length > 0 ? (
                  filteredModes.map((mode, index) => (
                    <tr
                      key={mode.id}
                      className="border-b last:border-none hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6">{index + 1}</td>
                      <td className="py-4 px-6">{mode.name}</td>
                      <td className="py-4 px-6 text-center space-x-3">
                        <button
                          onClick={() => handleEditMode(mode)}
                          className="text-blue-500 hover:text-blue-800 flex items-center gap-1"
                        >
                          <FiEdit /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(mode.id)}
                          className="text-red-600 hover:text-red-800 flex items-center gap-1"
                        >
                          <FiTrash2 /> Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="py-8 text-center text-gray-600">
                      No modes of payment found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
            <div className="bg-white p-8 rounded-lg w-full max-w-md shadow-lg">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">
                  {editMode ? "Edit Mode of Payment" : "Add Mode of Payment"}
                </h2>
                <button
                  onClick={() => setModalOpen(false)}
                  className="text-gray-500 hover:text-gray-800"
                >
                  <AiOutlineClose className="text-2xl" />
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    Mode Name
                  </label>
                  <input
                    type="text"
                    value={modeName}
                    onChange={(e) => setModeName(e.target.value)}
                    required
                    className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editMode ? "Update" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModeOfPaymentPage;
